import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Bell, Check } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Petit "il y a X" dans la langue de l'utilisateur (léger, sans dépendance).
function timeAgo(iso, lang) {
  try {
    const now = Date.now();
    const then = new Date(iso).getTime();
    const s = Math.max(0, Math.round((now - then) / 1000));
    const m = Math.round(s / 60), h = Math.round(m / 60), d = Math.round(h / 24);
    const L = (lang || 'fr').toLowerCase();
    const jp = L.indexOf('ja') === 0 || L.indexOf('jp') === 0;
    const en = L.indexOf('en') === 0;
    if (s < 60) return jp ? 'たった今' : en ? 'just now' : "à l'instant";
    if (m < 60) return jp ? `${m}分前` : en ? `${m}m ago` : `il y a ${m} min`;
    if (h < 24) return jp ? `${h}時間前` : en ? `${h}h ago` : `il y a ${h} h`;
    return jp ? `${d}日前` : en ? `${d}d ago` : `il y a ${d} j`;
  } catch (e) { return ''; }
}

// Une couleur de pastille par catégorie, pour repérer le type d'un coup d'œil.
const DOT = {
  booking: '#00F0FF', payment: '#F9004D', payout: '#22c55e', review: '#eab308',
  message: '#a855f7', host: '#38bdf8', wallet: '#22c55e', system: '#9ca3af',
};

export default function NotificationBell() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [unread, setUnread] = React.useState(0);

  const load = React.useCallback(function () {
    if (!isAuthenticated) return;
    axios.get(API_URL + '/api/notifications', { withCredentials: true })
      .then(function (r) { setItems(r.data.items || []); setUnread(r.data.unread || 0); })
      .catch(function () { /* silencieux : la cloche ne doit jamais casser la nav */ });
  }, [isAuthenticated]);

  // Charge à la connexion, puis rafraîchit toutes les 60 s (et à l'ouverture).
  React.useEffect(function () {
    if (!isAuthenticated) { setItems([]); setUnread(0); return; }
    load();
    const id = setInterval(load, 60000);
    return function () { clearInterval(id); };
  }, [isAuthenticated, load]);

  React.useEffect(function () { if (open) load(); }, [open, load]);

  function openItem(n) {
    setOpen(false);
    if (!n.read) {
      setUnread(function (u) { return Math.max(0, u - 1); });
      setItems(function (list) { return list.map(function (x) { return x.notification_id === n.notification_id ? Object.assign({}, x, { read: true }) : x; }); });
      axios.post(API_URL + '/api/notifications/' + n.notification_id + '/read', {}, { withCredentials: true }).catch(function () {});
    }
    if (n.link) navigate(n.link);
  }

  function markAll() {
    setUnread(0);
    setItems(function (list) { return list.map(function (x) { return Object.assign({}, x, { read: true }); }); });
    axios.post(API_URL + '/api/notifications/read-all', {}, { withCredentials: true }).catch(function () {});
  }

  // Traduction par TYPE, avec repli sur le texte stocké : la cloche affiche
  // toujours quelque chose, même pour un type qu'elle ne connaît pas encore.
  function tr(n, field, fallback) {
    const key = 'notifications.types.' + n.type + '.' + field;
    const out = t(key, Object.assign({}, n.data));
    return out === key ? (fallback || '') : out;
  }

  if (!isAuthenticated) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 text-zinc-300 hover:text-white transition-colors" aria-label="Notifications" data-testid="notif-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F9004D] text-white text-[11px] font-bold flex items-center justify-center" data-testid="notif-badge">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-zinc-900 border-white/10 text-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <span className="font-bold">{t('notifications.title')}</span>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-[#00F0FF] hover:underline flex items-center gap-1" data-testid="notif-mark-all">
              <Check className="h-3 w-3" /> {t('notifications.markAllRead')}
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-zinc-500 text-sm">{t('notifications.empty')}</div>
          ) : items.map(function (n) {
            return (
              <button
                key={n.notification_id}
                onClick={function () { openItem(n); }}
                className={'w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors flex gap-3 ' + (n.read ? 'opacity-60' : '')}
                data-testid="notif-item"
              >
                <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: DOT[n.category] || DOT.system }} />
                <span className="flex-1 min-w-0">
                  <span className={'block text-sm ' + (n.read ? 'text-zinc-300' : 'text-white font-semibold')}>{tr(n, 'title', n.title)}</span>
                  {(tr(n, 'body', n.body)) && <span className="block text-xs text-zinc-400 mt-0.5">{tr(n, 'body', n.body)}</span>}
                  <span className="block text-[11px] text-zinc-500 mt-1">{timeAgo(n.created_at, i18n.language)}</span>
                </span>
                {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#F9004D] shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
